import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IAuthResponse, ILogin, IRegister } from "@shared/interfaces";
import { IChangePassword, IEditUser, IUser } from "@shared/interfaces/user.interface";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { JwtService } from "./jwt.service";

@Injectable()
export class UserService {
  private user$ = new BehaviorSubject<IUser | null>(null);
  private passwordCheck$ = new Subject<{type: number, pass: string}>();

  constructor(private http: HttpClient,
              private jwtService: JwtService) {}

  isLogged() {
    return !!this.user$.value;
  }

  getCookieAccept() {
    return localStorage.getItem('cookie_accept');
  }

  setCookieAccept() {
    localStorage.setItem('cookie_accept', 'true');
  }

  mailIsVerify() {
    return this.user$.value?.email_verified_at;
  }

  isAdmin() {
    return this.user$.value?.permission_group.name === 'operator';
  }

  listenPasswordCheck() {
    return this.passwordCheck$.asObservable();
  }

  triggerToPasswordCheck(check: {type: number, pass: string}) {
    this.passwordCheck$.next(check);
  }

  sendVerifyMail() {
    let url = environment.apiUrl + '/auth/sendVerifyLink';
    url = url.replace('/v1', '');
    return this.http.post(url, {});
  }

  register(body: IRegister): Observable<IAuthResponse> {
    let url = environment.apiUrl + '/auth/register';
    url = url.replace('/v1', '');
    return this.http.post<IAuthResponse>(url, body);
  }

  login(body: ILogin): Observable<IAuthResponse> {
    let url = environment.apiUrl + '/auth/login';
    url = url.replace('/v1', '');
    return this.http.post<IAuthResponse>(url, body);
  }
  
  checkPassword(password: string) {
    let url = environment.apiUrl + '/user/checkPassword';
    return this.http.post(url, {
      password
    })
  }

  forgetPassword(email: string) {
    let url = environment.apiUrl + '/auth/forgot-password';
    url = url.replace('/v1', '');
    return this.http.post(url, {
      email
    })
  }

  resetPassword(email: string, password: string, token: string) {
    let url = environment.apiUrl + `/auth/password-reset/${token}`;
    url = url.replace('/v1', '');
    return this.http.post(url, {
      email,
      token,
      password_confirmation: password,
      password
    })
  }

  editUser(body: IEditUser) {
    let url = environment.apiUrl + '/user/edit';
    return this.http.post(url, body);
  }

  logout() {
    let url = environment.apiUrl + '/auth/logout';
    url = url.replace('/v1', '');
    return this.http.post(url, {});
  }

  unlogin() {
    this.jwtService.removeTocken();
    this.setUserData(null);
  }

  loadUserData(): Observable<IUser | null> {
    const url = environment.apiUrl + '/profile';
    return this.http.get<IUser | null>(url);
  }
  
  getUserData(): Observable<IUser | null> {
    return this.user$.asObservable();
  }

  getUserSync(): IUser | null {
    return this.user$.value;
  }

  setUserData(user: IUser | null) {
    this.user$.next(user);
  }

  changePassword(body: IChangePassword) {
    const url = environment.apiUrl + '/user/changePassword';
    return this.http.post(url, body);
  }
}